import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Material3IconButton, Material3Button } from '../ui/Material3Button';
import { useColors } from '../../contexts/ColorContext';
import { Material3NavigationProps } from '../../types/material3';
import clsx from 'clsx';

const navigationItems = [
  { id: 'home', path: '/', icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>, labelKey: 'navigation.home' },
  { id: 'planner', path: '/whats-on-now', icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 00-2 2v13a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2zm0 15H5V9h14v10zm0-12H5V6h14v1z"/></svg>, labelKey: 'navigation.now' },
  { id: 'codex', path: '/codex/', icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>, labelKey: 'navigation.guide' },
  { id: 'toolbox', path: '/toolbox', icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M21 6h-3.17l-1.84-2.76A2 2 0 0014.32 2H9.68a2 2 0 00-1.67.89L6.17 6H3a2 2 0 00-2 2v2h22V8a2 2 0 00-2-2zm0 5H3v9a2 2 0 002 2h14a2 2 0 002-2v-9zm-8 6h-2v-4h2v4z"/></svg>, labelKey: 'navigation.conditions' }
];

const localePrefixFromPath = (pathname: string) => {
  const match = pathname.match(/^\/(en|it|es|fr|de)(?=\/|$)/);
  return match?.[0] || '';
};

const stripLocalePrefix = (pathname: string) => pathname.replace(/^\/(en|it|es|fr|de)(?=\/|$)/, '') || '/';

const withLocalePrefix = (path: string, pathname: string) => {
  const prefix = localePrefixFromPath(pathname);
  if (!prefix) return path;
  return path === '/' ? prefix : `${prefix}${path}`;
};

export const Material3BottomNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { dynamicColors } = useColors();
  const isActiveRoute = (path: string) => {
    const activePath = stripLocalePrefix(location.pathname);
    return path === '/' ? activePath === '/' : activePath === path || activePath.startsWith(`${path}/`);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 px-3 pt-2 nav-safe-bottom">
      <nav
        className="mx-auto flex h-[76px] max-w-md items-center gap-1.5 rounded-[24px] border border-outline/70 bg-white p-1.5 shadow-sm"
        style={{ backgroundColor: dynamicColors.surface, borderColor: dynamicColors.outline, boxShadow: '0 16px 32px -24px rgba(3, 4, 94, 0.45)' }}
      >
        {navigationItems.map((item) => {
          const isActive = isActiveRoute(item.path);
          return (
            <button
              key={item.id}
              onClick={() => navigate(withLocalePrefix(item.path, location.pathname))}
              className={clsx(
                'flex h-full min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-[20px] px-2 text-[0.68rem] font-bold leading-tight transition-all duration-150',
                isActive
                  ? 'bg-primary-50 text-primary-700 shadow-sm'
                  : 'text-on-surface-variant hover:bg-primary-500/10 hover:text-primary-700'
              )}
              aria-label={t(item.labelKey)}
              aria-current={isActive ? 'page' : undefined}
            >
              <span
                className={clsx(
                  'flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-150',
                  isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-current'
                )}
                aria-hidden="true"
              >
                {React.cloneElement(item.icon, { className: 'h-5 w-5' })}
              </span>
              <span className="max-w-full truncate">{t(item.labelKey)}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export const Material3NavigationRail: React.FC<{ expanded?: boolean; onToggle?: () => void }> = ({ expanded = false, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { dynamicColors } = useColors();
  const isActiveRoute = (path: string) => {
    const activePath = stripLocalePrefix(location.pathname);
    return path === '/' ? activePath === '/' : activePath === path || activePath.startsWith(`${path}/`);
  };

  return (
    <div style={{ backgroundColor: dynamicColors.surface, borderColor: dynamicColors.outline }} className={clsx('fixed bottom-0 left-0 top-0 z-40 border-r', expanded ? 'w-72' : 'w-20')}>
      <nav className="flex h-full flex-col py-6">
        <div className="px-4 pb-6">{onToggle && <Material3IconButton icon={<span className="text-xl">☰</span>} onClick={onToggle} aria-label={t('layout.toggleNavigationMenu')} size="lg" />}</div>
        <div className="flex-1 space-y-2 px-3">
          {navigationItems.map((item) => {
            const isActive = isActiveRoute(item.path);
            return (
              <Material3Button
                key={item.id}
                variant={isActive ? 'filled' : 'text'}
                size="md"
                onClick={() => navigate(withLocalePrefix(item.path, location.pathname))}
                fullWidth
                leftIcon={<div className={clsx('flex items-center justify-center', expanded && 'mr-3')}>{item.icon}</div>}
                className={clsx('!rounded-full', expanded ? '!justify-start !px-6 !py-4' : '!justify-center !px-4 !py-4')}
                aria-label={`${t(item.labelKey)} navigation`}
                aria-current={isActive ? 'page' : undefined}
              >
                {expanded ? <span className="text-sm font-medium">{t(item.labelKey)}</span> : undefined}
              </Material3Button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export const Material3AppBar: React.FC<{ title: string; subtitle?: string; showBack?: boolean; onBack?: () => void; actions?: React.ReactNode[]; centerTitle?: boolean }> = ({ title, subtitle, showBack = false, onBack, actions = [], centerTitle = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { dynamicColors } = useColors();
  const handleBack = () => onBack ? onBack() : navigate(-1);

  return (
    <div style={{ backgroundColor: dynamicColors.surface, borderColor: dynamicColors.outline }} className="sticky top-0 z-40 border-b">
      <div className="safe-padding">
        <header className="flex h-16 items-center px-4">
          {showBack && <Material3IconButton icon={<span className="text-xl">←</span>} onClick={handleBack} aria-label={t('actions.back')} className="mr-2" />}
          <div className={clsx('min-w-0 flex-1', centerTitle ? 'text-center' : 'text-left')}>
            <h1 className="headline-small line-clamp-1 font-medium text-on-surface">{title}</h1>
            {subtitle ? <p className="body-small mt-0.5 line-clamp-1 text-on-surface-variant">{subtitle}</p> : null}
          </div>
          {actions.length > 0 ? <div className="ml-4 flex items-center gap-1">{actions.map((action, index) => <div key={index}>{action}</div>)}</div> : null}
        </header>
      </div>
    </div>
  );
};

export const Material3Navigation: React.FC<Material3NavigationProps> = ({ variant = 'bottom' }) => {
  if (variant === 'bottom' || (typeof window !== 'undefined' && window.innerWidth < 768)) {
    return <Material3BottomNavigation />;
  }
  return <Material3NavigationRail expanded={typeof window !== 'undefined' ? window.innerWidth >= 1024 : false} />;
};

export { navigationItems };
